import { Form, Formik, type FormikErrors, type FormikHelpers, useField } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createTaxRecord, updateTaxRecord } from "@/features/transcripts/api";
import type { FiledStatus, ForWho, TaxForm, TaxRecord, TaxRecordInput } from "@/features/transcripts/types";
import { applyApiErrors } from "@/lib/form-errors";
import { centsToDollars, dollarsToCents } from "@/lib/utils";

const FORMS: TaxForm[] = ["1040", "941", "1120", "1120S", "940", "720", "706", "CIVPEN"];
const PERIODIC_FORMS: TaxForm[] = ["941", "720"];
const FILED_STATUSES: FiledStatus[] = ["Filed", "Not Filed", "SFR"];
const FILING_STATUSES = ["Single", "MFJ", "MFS", "HOH", "QW"];
const LABEL = "text-muted-foreground text-xs font-semibold uppercase";

/** Formik-bound shadcn Select. */
function SelectField({
  name,
  label,
  options,
  placeholder = "Select…",
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [field, meta, helpers] = useField<string>(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label className={LABEL}>{label}</Label>
      <Select value={field.value || undefined} onValueChange={(v) => helpers.setValue(v)}>
        <SelectTrigger className="w-full" aria-label={label} aria-invalid={hasError}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

interface Values {
  form: string;
  taxYear: string;
  quarter: string;
  forWho: ForWho;
  filedStatus: FiledStatus;
  filingStatus: string;
  balance: string;
  accruedInterest: string;
  accruedPenalty: string;
  lienCount: string;
  returnDueDate: string;
  returnFiledDate: string;
  assessmentDate: string;
  csedDate: string;
}

const currentYear = new Date().getFullYear();

const validationSchema = Yup.object({
  form: Yup.string().required("Form is required"),
  taxYear: Yup.number()
    .typeError("Enter a year")
    .required("Tax year is required")
    .min(1900, "Too early")
    .max(currentYear + 1, `No later than ${currentYear + 1}`),
  quarter: Yup.string().when("form", {
    is: (f: string) => PERIODIC_FORMS.includes(f as TaxForm),
    then: (s) => s.required("Quarter is required for this form"),
  }),
  filedStatus: Yup.string().required("Filed status is required"),
  balance: Yup.string(),
  accruedInterest: Yup.string().test("nonneg", "Cannot be negative", (v) => dollarsToCents(v ?? "") >= 0),
  accruedPenalty: Yup.string().test("nonneg", "Cannot be negative", (v) => dollarsToCents(v ?? "") >= 0),
  lienCount: Yup.number().typeError("Enter a number").min(0, "Cannot be negative"),
});

function toInitialValues(record: TaxRecord | null, forWho: ForWho): Values {
  return {
    form: record?.form ?? "",
    taxYear: record?.taxYear != null ? String(record.taxYear) : String(currentYear - 1),
    quarter: record?.quarter != null ? String(record.quarter) : "",
    forWho: record?.forWho ?? forWho,
    filedStatus: record?.filedStatus ?? "Filed",
    filingStatus: record?.filingStatus ?? "",
    balance: record ? centsToDollars(record.balanceCents) : "",
    accruedInterest: record ? centsToDollars(record.accruedInterestCents) : "",
    accruedPenalty: record ? centsToDollars(record.accruedPenaltyCents) : "",
    lienCount: String(record?.lienCount ?? 0),
    returnDueDate: record?.returnDueDate ?? "",
    returnFiledDate: record?.returnFiledDate ?? "",
    assessmentDate: record?.assessmentDate ?? "",
    csedDate: record?.estimatedCsed ?? "",
  };
}

function toPayload(v: Values): TaxRecordInput {
  const periodic = PERIODIC_FORMS.includes(v.form as TaxForm);
  return {
    form: v.form as TaxForm,
    taxYear: Number(v.taxYear),
    quarter: periodic && v.quarter ? Number(v.quarter) : null,
    forWho: v.forWho,
    filedStatus: v.filedStatus,
    filingStatus: v.form === "1040" ? v.filingStatus : "",
    balanceCents: dollarsToCents(v.balance),
    accruedInterestCents: dollarsToCents(v.accruedInterest),
    accruedPenaltyCents: dollarsToCents(v.accruedPenalty),
    lienCount: Number(v.lienCount) || 0,
    returnDueDate: v.returnDueDate || null,
    returnFiledDate: v.returnFiledDate || null,
    assessmentDate: v.assessmentDate || null,
    csedDate: v.csedDate || null,
  };
}

interface Props {
  clientId: string;
  hasSpouse: boolean;
  /** `null` = add; otherwise edit this record. */
  record: TaxRecord | null;
  forWho: ForWho;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function TaxPeriodFormDialog({
  clientId,
  hasSpouse,
  record,
  forWho,
  open,
  onOpenChange,
  onSaved,
}: Props) {
  const editing = record !== null;

  async function handleSubmit(values: Values, bag: FormikHelpers<Values>) {
    try {
      const payload = toPayload(values);
      if (editing) {
        await updateTaxRecord(clientId, record.id, payload);
      } else {
        await createTaxRecord(clientId, payload);
      }
      toast.success(editing ? "Tax period updated" : "Tax period added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save tax period." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Tax Period" : "Add Tax Period"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={toInitialValues(record, forWho)}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, isSubmitting }) => {
            const periodic = PERIODIC_FORMS.includes(values.form as TaxForm);
            return (
              <Form className="grid max-h-[70vh] gap-4 overflow-y-auto px-1">
                <div className="grid grid-cols-2 gap-4">
                  <SelectField
                    name="form"
                    label="Form"
                    options={FORMS.map((f) => ({ value: f, label: f }))}
                  />
                  <FormField name="taxYear" label="Tax Year" labelClassName={LABEL} inputMode="numeric" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {periodic && (
                    <SelectField
                      name="quarter"
                      label="Quarter"
                      options={[1, 2, 3, 4].map((q) => ({ value: String(q), label: `Q${q}` }))}
                    />
                  )}
                  {hasSpouse && (
                    <SelectField
                      name="forWho"
                      label="Taxpayer / Spouse"
                      options={[
                        { value: "T", label: "Taxpayer" },
                        { value: "S", label: "Spouse" },
                      ]}
                    />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <SelectField
                    name="filedStatus"
                    label="Filed Status"
                    options={FILED_STATUSES.map((s) => ({ value: s, label: s }))}
                  />
                  {values.form === "1040" && (
                    <SelectField
                      name="filingStatus"
                      label="Filing Status"
                      options={FILING_STATUSES.map((s) => ({ value: s, label: s }))}
                    />
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <FormField name="balance" label="Balance ($)" labelClassName={LABEL} inputMode="decimal" />
                  <FormField name="accruedInterest" label="Interest ($)" labelClassName={LABEL} inputMode="decimal" />
                  <FormField name="accruedPenalty" label="Penalty ($)" labelClassName={LABEL} inputMode="decimal" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField name="lienCount" label="Liens (NFTL)" labelClassName={LABEL} inputMode="numeric" />
                  <FormField name="csedDate" label="CSED (manual)" labelClassName={LABEL} type="date" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <FormField name="returnDueDate" label="Due Date" labelClassName={LABEL} type="date" />
                  <FormField name="returnFiledDate" label="Filed Date" labelClassName={LABEL} type="date" />
                  <FormField name="assessmentDate" label="Assessed" labelClassName={LABEL} type="date" />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {editing ? "Save Changes" : "Add Tax Period"}
                  </Button>
                </DialogFooter>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
